import { Injectable, inject } from '@angular/core';
import { Action, Selector, State, StateContext, StateToken } from '@ngxs/store';
import { Observable, catchError, of, tap, throwError } from 'rxjs';
import { InvoicesService } from '../../../features/invoices/invoices.service';

import { EFavorite, EFavoriteType } from '../../enums/favorite.enum';
import { IInvoice } from '../../interfaces/invoice.interface';
import { BaseResponse } from '../../models/base-response.model';
import { ClearInvoice, LoadInvoice, MarkAsFavorite, SmartBanner, UpdateCurrentInvoice } from './invoice.actions';
import { InvoiceStateModel } from './invoice.models';

const INVOICE_STATE_TOKEN = new StateToken<InvoiceStateModel>('invoice');

const defaultInvoiceState = {
  invoice: null,
} as InvoiceStateModel;

@State<InvoiceStateModel>({
  name: INVOICE_STATE_TOKEN,
  defaults: defaultInvoiceState,
})
@Injectable()
export class InvoiceState {
  private invoicesService = inject(InvoicesService);

  @Selector()
  static currentInvoice(state: InvoiceStateModel): IInvoice | null {
    return state.invoice;
  }

  @Selector()
  static currentInvoiceId(state: InvoiceStateModel): string | null {
    return state.invoice?.id || null;
  }

  @Action(LoadInvoice)
  loadInvoice(ctx: StateContext<InvoiceStateModel>, action: LoadInvoice): Observable<BaseResponse<IInvoice> | null> {
    const currentState = ctx.getState();

    // Early return if invoice is already cached and cache is enabled
    if (currentState.invoice && currentState.invoice.id === action.id && action.isCacheEnabled) {
      return of(null);
    }

    // Execute API call
    return this.invoicesService.queryInvoice(action.id).pipe(
      tap((response: BaseResponse<IInvoice>) => {
        if (response.isSuccess && response.result) {
          ctx.patchState({
            invoice: response.result,
          });
        }
      }),
      catchError((error) => {
        console.error('Error loading invoice:', error);
        // Re-throw error so ofActionErrored triggers if needed
        return throwError(() => error);
      }),
    );
  }

  @Action(ClearInvoice)
  async clearInvoice(ctx: StateContext<InvoiceStateModel>) {
    ctx.setState({
      invoice: null,
    });
  }

  @Action(UpdateCurrentInvoice)
  updateCurrentInvoice(ctx: StateContext<InvoiceStateModel>, action: UpdateCurrentInvoice) {
    ctx.patchState({
      invoice: action.invoice,
    });
  }

  @Action(MarkAsFavorite)
  markAsFavorite(ctx: StateContext<InvoiceStateModel>, action: MarkAsFavorite) {
    const currentState = ctx.getState();

    // Guard: Only proceed if we have an invoice and it matches the action ID
    if (!currentState.invoice || currentState.invoice.id !== action.invoiceId) {
      return;
    }

    // Toggle favorite status
    const newFavoriteStatus = currentState.invoice.favorite === EFavorite.no ? EFavorite.yes : EFavorite.no;

    // Update state immediately
    ctx.patchState({
      invoice: {
        ...currentState.invoice,
        favorite: newFavoriteStatus as EFavoriteType,
      },
    });
  }

  @Action(SmartBanner)
  smartBanner(ctx: StateContext<InvoiceStateModel>, action: SmartBanner) {
    const currentState = ctx.getState();

    // Guard: Only proceed if we have an invoice and it matches the action ID
    if (!currentState.invoice || currentState.invoice.id !== action.invoiceId) {
      return;
    }

    // Toggle smart banner status
    const newShowSmartBanner = !currentState.invoice.showSmartBanner;

    // Update state immediately
    ctx.patchState({
      invoice: {
        ...currentState.invoice,
        showSmartBanner: newShowSmartBanner,
      },
    });
  }
}
